import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import ShareIcon from '@mui/icons-material/Share';
import { Box, DialogTitle, DialogContent, Grid, DialogActions } from '@mui/material';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  IComposableDashboard,
  IComposableDashboardCollection,
  IPinnedDashboard,
  IPinnedDashboardCollection,
  IRecruiterTeamNameAutoCompleteOption,
} from 'wisestep-api';
import {
  WsDialog,
  WsTypography,
  WsButtonGroup,
  WsButton,
  WsAutoComplete,
  WsTypoVariants,
} from 'wisestep-base';

import { IconForDialog } from '../../../../components';
import { WsTeamsFilter } from '../../../../components/filters/components';
import RecruiterSuggestions from '../../../../components/filters/components/RecruiterSuggestions';
import { defaultRoles } from '../../../accessControl/AddRoleAccessControl';

interface IShareDashboardDialogProps {
  dashboard: IComposableDashboard | IComposableDashboardCollection;
  onCloseDialog: () => void;
  openShareDialog: boolean;
  onSave: (dashboard: IComposableDashboard | IComposableDashboardCollection) => void;
  isAssignFlow: boolean;
  sharedToTeams: IRecruiterTeamNameAutoCompleteOption[];
  selectedPinnedDashboard: IPinnedDashboard | IPinnedDashboardCollection | undefined;
}
const ShareDashboardDialog = ({
  dashboard,
  onCloseDialog,
  openShareDialog,
  onSave,
  isAssignFlow = false,
  sharedToTeams,
  selectedPinnedDashboard,
}: IShareDashboardDialogProps) => {
  const methods = useForm<
    (IComposableDashboard | IComposableDashboardCollection) & {
      sharedToTeams: IRecruiterTeamNameAutoCompleteOption[];
    }
  >({
    defaultValues: isAssignFlow
      ? {
          ...dashboard,
          sharedToTeams: sharedToTeams,
          sharedWithRecruiters: selectedPinnedDashboard?.users,
          sharedWithTeams: selectedPinnedDashboard?.teams,
          sharedWithRoles: selectedPinnedDashboard?.roles,
        }
      : {
          ...dashboard,
          sharedToTeams: sharedToTeams,
          accessLevel: dashboard?.accessLevel == 'restricted' ? 'private' : dashboard?.accessLevel,
        },
  });

  const { handleSubmit, setValue, getValues } = methods;
  setValue('sharedToTeams', sharedToTeams ?? []);
  useEffect(() => setValue('sharedToTeams', sharedToTeams ?? []), [setValue, sharedToTeams]);
  const accessLevels = ['Private', 'Public', 'Shared'];
  const [accessLevelsDefaultValue] = useState(
    accessLevels.findIndex((level) => level.toLowerCase() == getValues('accessLevel'))
  );
  const [isShared, setIsShared] = useState<boolean>(isAssignFlow || accessLevelsDefaultValue == 2);

  const onSubmit = (
    data: (IComposableDashboard | IComposableDashboardCollection) & {
      sharedToTeams: IRecruiterTeamNameAutoCompleteOption[];
    }
  ) => {
    const updateDashboardState = { ...dashboard };
    if (
      (dashboard.accessLevel == 'restricted' || dashboard.accessLevel == 'private') &&
      !isAssignFlow
    ) {
      updateDashboardState.sharedWithTeams =
        updateDashboardState.sharedWithRecruiters =
        updateDashboardState.sharedWithRoles =
          [];
    } else {
      updateDashboardState.sharedWithTeams = data.sharedToTeams?.map((team) => team?.id);
      updateDashboardState.sharedWithRecruiters = data?.sharedWithRecruiters;
      updateDashboardState.sharedWithRoles = data.sharedWithRoles;
    }
    onSave(updateDashboardState);
  };

  const handleAccessLevel = (index: number, value: string) => {
    if (index === 2 && value === 'Shared') {
      setIsShared(!isShared);
      dashboard.accessLevel = 'shared';
    } else {
      isShared && setIsShared(false);
      dashboard.accessLevel = index == 0 ? 'restricted' : 'public';
    }
  };
  return (
    <WsDialog openDialog={openShareDialog} onClose={onCloseDialog}>
      <FormProvider {...methods}>
        <Box pt={2} component="form" onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>
            {isAssignFlow ? (
              <IconForDialog icon={<AssignmentIndIcon />} />
            ) : (
              <IconForDialog icon={<ShareIcon />} />
            )}
          </DialogTitle>
          <DialogContent>
            <Grid mb={1}>
              <WsTypography bold variant={WsTypoVariants.TITLE}>
                {'Select who can see the Dashboard'}
              </WsTypography>
            </Grid>
            {!isAssignFlow && (
              <WsButtonGroup
                inputTitle=""
                buttonsList={accessLevels}
                selectedIndexValue={handleAccessLevel}
                defaultValue={accessLevelsDefaultValue}
              />
            )}
            {isShared && (
              <Box pt={2}>
                <WsTypography>{`${isAssignFlow ? 'Assign to' : 'Share with'}`}</WsTypography>
                <Grid item xs={12} container display="flex" alignItems="center" pt={2}>
                  <Grid item xs={2}>
                    <WsTypography>Teams</WsTypography>
                  </Grid>
                  <Grid xs={10}>
                    <WsTeamsFilter
                      name="sharedToTeams"
                      defaultObjects={sharedToTeams}
                      defaultValue={sharedToTeams?.map((team) => team.name) ?? []}
                    />
                  </Grid>
                </Grid>
                <Grid item xs={12} container display="flex" alignItems="center" pt={2}>
                  <Grid item xs={2}>
                    <WsTypography>Users</WsTypography>
                  </Grid>
                  <Grid xs={10}>
                    <RecruiterSuggestions
                      name="sharedWithRecruiters"
                      multiple={true}
                      defaultValue={
                        (isAssignFlow
                          ? selectedPinnedDashboard?.users
                          : dashboard?.sharedWithRecruiters) ?? []
                      }
                    />
                  </Grid>
                </Grid>
                <Grid item xs={12} container display="flex" alignItems="center" pt={2}>
                  <Grid item xs={2}>
                    <WsTypography>Select Role</WsTypography>
                  </Grid>
                  <Grid xs={10}>
                    <WsAutoComplete
                      name="sharedWithRoles"
                      optionsInStringArray={defaultRoles?.map((role) => role.label)}
                      multiple={true}
                      initialValue={
                        (isAssignFlow
                          ? selectedPinnedDashboard?.roles
                          : dashboard?.sharedWithRoles) ?? []
                      }
                      onTagsChange={(tags: string[]) => methods.setValue('sharedWithRoles', tags)}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <WsButton onClick={onCloseDialog} variant="outlined">
              Cancel
            </WsButton>
            <WsButton variant="contained" type="submit">
              Save
            </WsButton>
          </DialogActions>
        </Box>
      </FormProvider>
    </WsDialog>
  );
};

export default ShareDashboardDialog;
